import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AsyncSelect from 'react-select/async';
import moment from 'moment';

import { popupData, PopupNames } from 'core';
import {
  usePopupStateOperations,
  useExpense,
  useExpenseCategories,
  useExpenseMutations,
} from 'core/hooks';
import { ExpenseSource, ExpenseCurrency } from 'core/graphql';
import { Button, Input, InputLabel, DateSelect } from 'atoms';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

export interface IExpenseFormPopupData {
  /** Edit when `expenseId` is set, create otherwise. */
  expenseId?: string;
  onSaved?: () => void;
}

interface ISelectOpt {
  value: string;
  label: string;
}

const CURRENCY_OPTIONS: ISelectOpt[] = Object.values(
  ExpenseCurrency,
).map((c) => ({ value: c, label: c }));

interface IFormState {
  date: string | null;
  paidAt: string | null;
  amount: string;
  currency: string;
  categorySlug: string;
  description: string;
  counterpartyName: string;
  counterpartyTaxNo: string;
  documentUrl: string;
  notes: string;
}

const emptyForm: IFormState = {
  date: null,
  paidAt: null,
  amount: '',
  // Phase 4: PLN is LearnIT's operating currency — always default new
  // expenses to it, regardless of `REACT_APP_LANG`. Edit mode hydrates from
  // the existing expense, so historical BYN/USD rows still render correctly.
  currency: ExpenseCurrency.Pln,
  categorySlug: '',
  description: '',
  counterpartyName: '',
  counterpartyTaxNo: '',
  documentUrl: '',
  notes: '',
};

const isValidUrl = (value: string): boolean =>
  !value || /^https:\/\/\S+$/i.test(value);

/**
 * Single create+edit modal for `Expense`. Fakturownia-sourced rows lock the
 * money fields (TZ §3.7) — the backend will silently drop attempts to mutate
 * them anyway, but disabling here keeps the user from being surprised.
 */
export const ExpenseFormPopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const data = popupData()[PopupNames.expenseForm];
  const isEdit = Boolean(data?.expenseId);

  const { expense } = useExpense(data?.expenseId);
  const { categories } = useExpenseCategories();
  const {
    createExpense,
    updateExpense,
    deleteExpense,
    loading,
  } = useExpenseMutations();

  const [form, setForm] = useState<IFormState>(emptyForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof IFormState, string>>
  >({});

  // Hydrate the form once we have the expense — the popup mounts before the
  // query resolves, so we listen for `expense` to flip.
  useEffect(() => {
    if (!isEdit) {
      setForm(emptyForm);
      return;
    }
    if (!expense) return;
    setForm({
      date: expense.date || null,
      paidAt: expense.paidAt || null,
      amount: expense.amount != null ? String(expense.amount) : '',
      currency: expense.currency || ExpenseCurrency.Pln,
      categorySlug: expense.category?.slug || '',
      description: expense.description || '',
      counterpartyName: expense.counterpartyName || '',
      counterpartyTaxNo: expense.counterpartyTaxNo || '',
      documentUrl: expense.documentUrl || '',
      notes: expense.notes || '',
    });
  }, [expense, isEdit]);

  const isFakturownia = expense?.source === ExpenseSource.Fakturownia;

  const categoryOptions = useMemo<ISelectOpt[]>(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const close = useCallback(() => {
    setForm(emptyForm);
    setErrors({});
    hidePopup(PopupNames.expenseForm);
  }, [hidePopup]);

  const setField = useCallback(
    <K extends keyof IFormState>(key: K, value: IFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const validate = useCallback((): boolean => {
    const next: typeof errors = {};
    if (!form.date)
      next.date = t('pages.fakturownia.expenseForm.errors.dateRequired');
    const num = parseFloat(form.amount);
    if (!form.amount) {
      next.amount = t('pages.fakturownia.expenseForm.errors.amountRequired');
    } else if (Number.isNaN(num) || num <= 0) {
      next.amount = t('pages.fakturownia.expenseForm.errors.amountPositive');
    }
    if (!form.currency)
      next.currency = t(
        'pages.fakturownia.expenseForm.errors.currencyRequired',
      );
    if (form.documentUrl && !isValidUrl(form.documentUrl)) {
      next.documentUrl = t('pages.fakturownia.expenseForm.errors.urlInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form, t]);

  const onSubmit = useCallback(async () => {
    if (!validate()) return;
    const amount = parseFloat(form.amount);
    const common = {
      date: form.date as string,
      currency: form.currency,
      categorySlug: form.categorySlug || undefined,
      description: form.description || undefined,
      counterpartyName: form.counterpartyName || undefined,
      counterpartyTaxNo: form.counterpartyTaxNo || undefined,
      documentUrl: form.documentUrl || undefined,
      paidAt: form.paidAt || undefined,
      notes: form.notes || undefined,
    };

    let ok = false;
    if (isEdit && data?.expenseId) {
      ok = await updateExpense({
        id: data.expenseId,
        ...common,
        amount,
      });
    } else {
      ok = await createExpense({
        ...common,
        amount,
      });
    }
    if (ok) {
      data?.onSaved?.();
      close();
    }
  }, [validate, form, isEdit, data, updateExpense, createExpense, close]);

  const onDelete = useCallback(async () => {
    if (!data?.expenseId) return;
    const ok = await deleteExpense(data.expenseId);
    if (ok) {
      data.onSaved?.();
      close();
    }
  }, [data, deleteExpense, close]);

  if (!data) return null;

  const title = isEdit
    ? t('pages.fakturownia.expenseForm.titleEdit', {
        pk: expense?.pk || data.expenseId,
      })
    : t('pages.fakturownia.expenseForm.titleCreate');

  return (
    <PopupContainer width="720px" title={title} onClose={close}>
      <FormGrid>
        <Field>
          <InputLabel hasError={Boolean(errors.date)}>
            {t('pages.fakturownia.expenseForm.fields.date')}
          </InputLabel>
          <DateSelect
            value={form.date ? moment(form.date).toDate() : null}
            onChange={(d) =>
              setField('date', d ? moment(d).format('YYYY-MM-DD') : null)
            }
            placeholder="YYYY-MM-DD"
            hasError={Boolean(errors.date)}
            disabled={isFakturownia}
          />
          {isFakturownia ? (
            <LockedHint>
              {t('pages.fakturownia.expenseForm.lockedHint')}
            </LockedHint>
          ) : null}
        </Field>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.paidAt')}
          </InputLabel>
          <DateSelect
            value={form.paidAt ? moment(form.paidAt).toDate() : null}
            onChange={(d) =>
              setField('paidAt', d ? moment(d).format('YYYY-MM-DD') : null)
            }
            placeholder="YYYY-MM-DD"
          />
        </Field>

        <Field>
          <InputLabel hasError={Boolean(errors.amount)}>
            {t('pages.fakturownia.expenseForm.fields.amount')}
          </InputLabel>
          <Input
            type="number"
            value={form.amount}
            onChange={({ target: { value } }) => setField('amount', value)}
            hasError={Boolean(errors.amount)}
            disabled={isFakturownia}
            financialNumber
          />
        </Field>

        <Field>
          <InputLabel hasError={Boolean(errors.currency)}>
            {t('pages.fakturownia.expenseForm.fields.currency')}
          </InputLabel>
          <SelectNative
            value={form.currency}
            onChange={(e) => setField('currency', e.target.value)}
            disabled={isFakturownia}
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectNative>
        </Field>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.category')}
          </InputLabel>
          <AsyncSelect
            cacheOptions
            defaultOptions={categoryOptions}
            value={
              form.categorySlug
                ? categoryOptions.find((c) => c.value === form.categorySlug) ||
                  null
                : null
            }
            onChange={(v) =>
              setField('categorySlug', (v as ISelectOpt | null)?.value || '')
            }
            loadOptions={() => Promise.resolve(categoryOptions)}
            isClearable
          />
        </FieldFull>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.description')}
          </InputLabel>
          <Input
            type="text"
            value={form.description}
            onChange={({ target: { value } }) => setField('description', value)}
          />
        </FieldFull>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.counterpartyName')}
          </InputLabel>
          <Input
            type="text"
            value={form.counterpartyName}
            onChange={({ target: { value } }) =>
              setField('counterpartyName', value)
            }
          />
        </Field>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.counterpartyTaxNo')}
          </InputLabel>
          <Input
            type="text"
            value={form.counterpartyTaxNo}
            onChange={({ target: { value } }) =>
              setField('counterpartyTaxNo', value)
            }
          />
        </Field>

        <FieldFull>
          <InputLabel hasError={Boolean(errors.documentUrl)}>
            {t('pages.fakturownia.expenseForm.fields.documentUrl')}
          </InputLabel>
          <Input
            type="text"
            value={form.documentUrl}
            onChange={({ target: { value } }) => setField('documentUrl', value)}
            hasError={Boolean(errors.documentUrl)}
          />
        </FieldFull>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.notes')}
          </InputLabel>
          <TextArea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            rows={3}
          />
        </FieldFull>
      </FormGrid>

      <Actions>
        {isEdit && expense?.source === ExpenseSource.Manual ? (
          <Button
            onClick={onDelete}
            variant="outline"
            colors={{ backgroundColor: Colors.White, color: Colors.Red }}
            disabled={loading}
          >
            {t('pages.fakturownia.expenseForm.delete')}
          </Button>
        ) : null}
        <Spacer />
        <Button
          onClick={close}
          variant="outline"
          colors={{ backgroundColor: Colors.White, color: Colors.Blue }}
        >
          {t('pages.fakturownia.expenseForm.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          variant="primary"
          loading={loading}
          disabled={loading}
          colors={{ backgroundColor: Colors.Blue, color: Colors.White }}
        >
          {t('pages.fakturownia.expenseForm.save')}
        </Button>
      </Actions>
    </PopupContainer>
  );
});

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding-top: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const LockedHint = styled.div`
  font-size: 11px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  padding: 6px 8px;
  border-radius: 6px;
`;

const SelectNative = styled.select`
  height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 0;
  padding: 0 12px;
  background: #fff;
  font-size: 14px;
  outline: none;

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid #d1d5db;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: #2563eb;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
`;

const Spacer = styled.div`
  flex: 1;
`;
